<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  avatarCreate,
  avatarUpdate,
  avatarDelete,
} from "@/common/api/rest/accessory/avatar";

import { accessoryPatch } from "@/common/api/rest/accessory/accessory";

import type { AccessoryType } from "@/common/types/accessory/accessory";
import type { TranslationFieldsType } from "@/common/types/translation";

import FormAccessory from "@/components/forms/form-accessory.vue";

import { ListTranslate } from "@/components";

import { baseAvatar, baseConfirm } from "@/common/components";

const { t, locale } = useI18n();
const route = useRoute();

const emit = defineEmits(["update:data"]);

const props = defineProps<{
  accessory: AccessoryType | null;
  accessoryId: string;
}>();

const showAvatarDel = ref(false);

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    label: "common.labels.name",
    value: "",
  },
  {
    type: "textarea",
    field: "description",
    label: "common.labels.description",
    value: "",
  },
]);

const type = computed(() => route.params.type as string);

function patchData(payload: Partial<AccessoryType>) {
  accessoryPatch(props.accessoryId, payload)
    .then((response) => {
      const { success, message } = response;

      if (!success) {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// AVATAR
function delAvatar() {
  showAvatarDel.value = true;
}

function delAvatarConfirm() {
  if (!props.accessory?.avatar) {
    return;
  }

  store.commit("SET_LOADING", true);

  avatarDelete(props.accessoryId)
    .then((response) => {
      const { success, message } = response;

      toast({ message: message, type: success ? "success" : "error" });

      emit("update:data");
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      showAvatarDel.value = false;
      store.commit("SET_LOADING", false);
    });
}
</script>

<template>
  <div class="flex justify-center">
    <base-avatar
      position="center"
      size="15rem"
      :is-edit="true"
      :show-edit="false"
      :show-upload="false"
      :show-image="true"
      :url="props.accessory?.avatar?.large"
      :aspect-ratio="1"
      border-radius="0.5rem"
      @del="delAvatar()"
    />
  </div>

  <form-accessory
    :data="props.accessory"
    :type="type"
    :is-patch="true"
    @update:data="patchData($event)"
  />

  <list-translate
    :owner-id="accessoryId"
    :language="locale.toLowerCase()"
    :fields="translationFields"
  />

  <base-confirm
    :show="showAvatarDel"
    :title="t('common.modals.titles.remove_image').toUpperCase()"
    :message="t('common.modals.messages.remove_image')"
    @cancel="showAvatarDel = false"
    @confirm="delAvatarConfirm()"
  />
</template>
